/**
 * PresetPicker — a checkbox group over the known preset bundles (STAFF_PRESETS, mirror of the server
 * rbac config). The server validates the chosen presets against the live config on write; this only
 * drives the picker UI. Labels are i18n-resolved (admin.team.preset.<id>). RTL-safe (flex + logical gaps).
 */
import { useT } from "@/app";
import { STAFF_PRESETS, type StaffPreset } from "../caps";

export interface PresetPickerProps {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

export function PresetPicker({ value, onChange, disabled }: PresetPickerProps) {
  const { t } = useT();
  const set = new Set(value);

  const toggle = (preset: StaffPreset) => {
    const next = new Set(set);
    if (next.has(preset)) next.delete(preset);
    else next.add(preset);
    onChange([...next]);
  };

  return (
    <div
      role="group"
      style={{
        display: "grid",
        gap: "var(--nv-s-2)",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      }}
    >
      {STAFF_PRESETS.map((preset) => (
        <label
          key={preset}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--nv-s-2)",
            padding: "var(--nv-s-2)",
            border: "1px solid var(--nv-border-subtle, var(--nv-line-faint))",
            borderRadius: "var(--nv-r-sm)",
            cursor: disabled ? "not-allowed" : "pointer",
            background: set.has(preset) ? "var(--nv-accent-soft, var(--nv-azure-soft))" : "transparent",
          }}
        >
          <input
            type="checkbox"
            checked={set.has(preset)}
            disabled={disabled}
            onChange={() => toggle(preset)}
          />
          <span>{t(`admin.team.preset.${preset}`)}</span>
        </label>
      ))}
    </div>
  );
}
